import { Button, Group, Text } from "@mantine/core";
import type { DataTableColumn } from "mantine-datatable";
import BasePage from "../BasePage";
import { useEffect, useState } from "react";
import { coreApi } from "../../utils/CoreAPI";
import { TableOverlay } from "../../components/LoadingOverlay";
import ResponsiveTable from "../../components/ResponsiveTable";
import { IconLogout, IconRefresh } from "@tabler/icons-react";
import Employee from "../../interfaces/Employee";
import { useNavigate } from "react-router";

function InactiveEmployeesPage() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [employeeData, setEmployeeData] = useState<Employee[]>([]);
    const [dataLength, setDataLength] = useState(0);
    const [selectedRows, setSelectedRows] = useState<React.Key[]>([]);

    const columns: DataTableColumn<Employee>[] = [
        { accessor: "id", title: "ID", sortable: true },
        { accessor: "displayName", title: "Name", sortable: true },
        { accessor: "EmployeePrincipalName", title: "E-Mail", sortable: true },
        { accessor: "jobTitle", title: "Title", sortable: true },
    ];

    const getActiveEmployees = async (forceSync = false) => {
        setLoading(true);

        const response = await coreApi.get(
            `/graph/active-employees${forceSync ? "?cache_bust=1" : ""}`
        );

        setEmployeeData(response.data);
        setDataLength(response.data.length);
        setLoading(false);
    };

    const handleEmployeeViewDetails = (employeeId: React.Key) => {
        const found = employeeData.find((employee) => employee.id === employeeId);

        if (!found) return;

        const employee: Employee = {
            id: found.id,
            displayName: found.displayName,
            userPrincipalName: found.userPrincipalName,
            jobTitle: found.jobTitle,
        };

        navigate(`Employee/${employeeId}`, { state: { employee } });
    };

    useEffect(() => {
        getActiveEmployees();
    }, []);

    return (
        <BasePage
            title="Offboarding"
            breadcrumbs={[{ label: "Offboarding" }]}
            fullWidth
        >
            <TableOverlay visible={loading}>
                <Group
                    justify="space-between"
                    style={{
                        borderBottom: "1px solid var(--border-color)",
                    }}
                >
                    <Group gap={0}>
                        <Button
                            className="unify-btn-subtle unify-border-0"
                            leftSection={<IconLogout size="1.125rem" />}
                            onClick={() => {
                                handleEmployeeViewDetails(selectedRows[0]);
                            }}
                            disabled={selectedRows.length !== 1}
                        >
                            Offboard
                        </Button>

                        <Button
                            className="unify-btn-subtle unify-border-0"
                            leftSection={<IconRefresh size="1.125rem" />}
                            onClick={() => {
                                getActiveEmployees(true);
                            }}
                        >
                            Sync
                        </Button>
                    </Group>

                    <Text size="sm" c="var(--text-dimmed)">
                        {dataLength} employees
                    </Text>
                </Group>

                <ResponsiveTable
                    data={employeeData}
                    columns={columns}
                    gridRowId={(row) => row.id}
                    rowSelectionModel={selectedRows}
                    onSelectionChange={setSelectedRows}
                    gridSortModel={{
                        columnAccessor: "displayName",
                        direction: "asc",
                    }}
                />
            </TableOverlay>
        </BasePage>
    );
}

export default InactiveEmployeesPage;